import { memo } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Grid,
  ImageListItem,
  ImageList,
} from '@mui/material'
import CachedImage from '../CachedImage'
import { getStatusColor } from '../../utils/productManagementUtils'
import { formatDate } from '../../utils/dateFormat'

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  )
}

function ViewProductDialog({ open, product, onClose }) {
  const productImage =
    product?.image_url?.portrait || product?.image_url?.logo || product?.image_url?.banner

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
        {product?.product_name || 'Product Details'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {product && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {productImage ? (
                <CachedImage
                  src={productImage}
                  alt={product.product_name}
                  style={{
                    width: 88,
                    height: 88,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #eee',
                  }}
                />
              ) : null}
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {product.product_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Article Code: {product.sku || '—'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={product.status}
                    color={getStatusColor(product.status)}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={product.is_purchasable !== false ? 'Purchasable' : 'Not purchasable'}
                    size="small"
                    sx={{
                      backgroundColor: product.is_purchasable !== false ? '#e8f5e9' : '#ffebee',
                      color: product.is_purchasable !== false ? '#2e7d32' : '#c62828',
                      fontWeight: 500,
                    }}
                  />
                  {product.category_name ? (
                    <Chip label={product.category_name} size="small" variant="outlined" />
                  ) : null}
                </Box>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailField label="Brand" value={product.brand || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="TP/MAP" value={product.tp_map || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Division" value={product.division_name || product.division_slug || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Unit of Measure" value={product.unit_of_measure || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Pack Size"
                  value={
                    product.pack_size != null && product.pack_size > 0
                      ? String(product.pack_size)
                      : '—'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Pack Type"
                  value={
                    product.pack_type
                      ? product.pack_type.charAt(0).toUpperCase() +
                        product.pack_type.slice(1)
                      : '—'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField label="Tax Category" value={product.tax_category || '—'} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Company Rate"
                  value={
                    product.company_rate != null && product.company_rate !== ''
                      ? `৳${
                          typeof product.company_rate === 'number'
                            ? product.company_rate.toFixed(2)
                            : parseFloat(product.company_rate || 0).toFixed(2)
                        }`
                      : '—'
                  }
                />
              </Grid>
              {product.barcode ? (
                <Grid item xs={12} sm={6}>
                  <DetailField label="Barcode" value={product.barcode} />
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <DetailField
                  label="Description"
                  value={product.description || 'No description provided'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Created By"
                  value={
                    product.created_by?.name
                      ? `${product.created_by.name}${
                          product.created_by.email ? ` (${product.created_by.email})` : ''
                        }`
                      : '—'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailField
                  label="Created At"
                  value={formatDate(product.created_at)}
                />
              </Grid>
            </Grid>

            {(product.image_url?.logo ||
              product.image_url?.portrait ||
              product.image_url?.banner ||
              (product.image_url?.additional_images &&
                product.image_url.additional_images.length > 0)) && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Product Images
                </Typography>
                <ImageList cols={3} gap={8}>
                  {product.image_url?.logo && (
                    <ImageListItem sx={{ bgcolor: '#f5f5f5' }}>
                      <Box sx={{ position: 'relative' }}>
                        <CachedImage
                          src={product.image_url.logo}
                          alt="Logo"
                          style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            p: 0.5,
                            fontSize: '0.6rem',
                          }}
                        >
                          Logo
                        </Typography>
                      </Box>
                    </ImageListItem>
                  )}

                  {product.image_url?.portrait && (
                    <ImageListItem sx={{ bgcolor: '#f5f5f5' }}>
                      <Box sx={{ position: 'relative' }}>
                        <CachedImage
                          src={product.image_url.portrait}
                          alt="Portrait"
                          style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            p: 0.5,
                            fontSize: '0.6rem',
                          }}
                        >
                          Portrait
                        </Typography>
                      </Box>
                    </ImageListItem>
                  )}

                  {product.image_url?.banner && (
                    <ImageListItem sx={{ bgcolor: '#f5f5f5' }}>
                      <Box sx={{ position: 'relative' }}>
                        <CachedImage
                          src={product.image_url.banner}
                          alt="Banner"
                          style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            p: 0.5,
                            fontSize: '0.6rem',
                          }}
                        >
                          Banner
                        </Typography>
                      </Box>
                    </ImageListItem>
                  )}

                  {product.image_url?.additional_images?.map((imageUrl, index) => (
                    <ImageListItem key={`additional-${index}`} sx={{ bgcolor: '#f5f5f5' }}>
                      <Box sx={{ position: 'relative' }}>
                        <CachedImage
                          src={imageUrl}
                          alt={`Additional ${index + 1}`}
                          style={{ height: '100px', objectFit: 'cover', width: '100%' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            p: 0.5,
                            fontSize: '0.6rem',
                          }}
                        >
                          Extra {index + 1}
                        </Typography>
                      </Box>
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(ViewProductDialog)
